import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { merge, Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { InfoConfirmComponent } from '../info-confirm/info-confirm.component';
import { TmModalData } from '../../entity/TmModalData';
import { TmModalCloseReason } from '../../entity/TmModalCloseReason';

/**
 * 組件可重用，只建立一個確認窗口透過服務更改顯示內容，
 * 開啟重用會在未關閉確認窗時暫存所有接收到且未關閉的訊息，並在點選關閉後播放堆線中下一個訊息
 *
 * 所有參數綁定和事件拋出是間皆是引用自實體的訊息確認窗
 */
@Component({
  selector: 'tm-reusable-info-confirm',
  templateUrl: './reusable-info-confirm.component.html',
  styleUrls: ['./reusable-info-confirm.component.css']
})
export class ReusableInfoConfirmComponent {
  protected _infoConfirm?: InfoConfirmComponent;
  /** 實體的訊息確認窗 */
  @ViewChild(InfoConfirmComponent, {static: false}) set infoConfirm(c: InfoConfirmComponent|undefined) {
    if (c) {
      this._infoConfirm = c;
      this.setPendingDatas();
    }
  }
  get infoConfirm(): InfoConfirmComponent | undefined { return this._infoConfirm; }
  protected _pendingTitle?: string;
  /** 表頭標題 */
  @Input() set title(t: string) {
    if (this.infoConfirm) {
      this.infoConfirm.title = t;
      this._pendingTitle = undefined;
    } else {
      this._pendingTitle = t;
    }
  }
  get title(): string | undefined {
    if (this.infoConfirm) {
      return this.infoConfirm.title;
    }    return this._pendingTitle || '';
  }
  protected _pendingMessage?: string;
  /** 主內文 */
  @Input() set message(m: string) {
    if (this.infoConfirm) {
      this.infoConfirm.message = m;
      this._pendingMessage = undefined;
    } else {
      this._pendingMessage = m;
    }
  }
  get message(): string | undefined {
    if (this.infoConfirm) {
      return this.infoConfirm.message;
    }
    return this._pendingMessage || '';
  }
  protected _pendingOkBtnLabel?: string;
  /** 確認按鈕標籤 */
  @Input() set okBtnLabel(ol: string) {
    if (this.infoConfirm) {
      this.infoConfirm.okBtnLabel = ol;
      this._pendingOkBtnLabel = undefined;
    } else {
      this._pendingOkBtnLabel = ol;
    }
  }
  get okBtnLabel(): string | undefined {
    if (this.infoConfirm) {
      return this.infoConfirm.okBtnLabel;
    }
    return this._pendingOkBtnLabel || '';
  }
  protected _pendingCancelBtnLabel?: string;
  /** 取消按鈕標籤 */
  @Input() set cancelBtnLabel(cl: string) {
    if (this.infoConfirm) {
      this.infoConfirm.cancelBtnLabel = cl;
      this._pendingCancelBtnLabel = undefined;
    } else {
      this._pendingCancelBtnLabel = cl;
    }
  }
  get cancelBtnLabel(): string | undefined {
    if (this.infoConfirm) {
      return this.infoConfirm.cancelBtnLabel;
    }
    return this._pendingCancelBtnLabel || '';
  }
  protected _pendingPanelClass?: string[];
  /** 自訂義彈窗的 css 類 */
  @Input() set panelClass(pc: string[]) {
    if (this.infoConfirm) {
      this.infoConfirm.panelClass = pc;
      this._pendingPanelClass = undefined;
    } else {
      this._pendingPanelClass = pc;
    }
  }
  get panelClass(): string[] | undefined {
    if (this.infoConfirm) {
      return this.infoConfirm.panelClass;
    }
    return this._pendingPanelClass || [];
  }
  protected _pendingModalConfig?: MatDialogConfig<TmModalData>;
  /** 彈窗設定
   * https://v12.material.angular.io/components/dialog/api#MatDialogConfig
   */
  @Input() set modalConfig(c: MatDialogConfig<TmModalData>) {
    if (this.infoConfirm) {
      this.infoConfirm.modalConfig = c;
      this._pendingModalConfig = undefined;
    } else {
      this._pendingModalConfig = c;
    }
  }
  get modalConfig(): MatDialogConfig<TmModalData> | undefined {
    if (this.infoConfirm) {
      return this.infoConfirm.modalConfig || {};
    }
    return this._pendingModalConfig || {};
  }
  /** 確認後關閉事件完成 */
  @Output() afterConfirm: EventEmitter<void> = new EventEmitter<void>();
  /** 非確認關閉事件完成 */
  @Output() afterCancel: EventEmitter<void> = new EventEmitter<void>();
  cachedInfos: any[] = [];

  open(message: string, param: { title: string, afterClose?: () => void, afterConfirm?: () => void }) {
    if (this.isSameWithLastest(message, param)) { return; }

    const cached: any = {
      message: (message),
      param: (param),
    };
    this.cachedInfos.push(cached);
    const cIndex = this.cachedInfos.length - 1;
    this.setInfo(cached, cIndex);

    if (this.infoConfirm && this.infoConfirm.modal && !this.infoConfirm.modal.isOpened) {
      this.infoConfirm.open();
    }
  }

  /**
   * 檢查新的訊息和目前快去中最後一則訊息，是否完全一致。
   * 如果一致應該丟棄訊息
   */
  private isSameWithLastest(message: string, param: { title: string, afterClose?: () => void, afterConfirm?: () => void }): boolean {
    if (!this.cachedInfos.length) { return false; }
    const lastestCached = this.cachedInfos[this.cachedInfos.length - 1];
    if (lastestCached.message !== message) { return false; }
    if (param.title !== lastestCached.param.title) { return false; }
    try {
      if (!param.afterClose && lastestCached.param.afterClose
        || param.afterClose && !lastestCached.param.afterClose
        || (param.afterClose && lastestCached.param.afterClose && param.afterClose.toString() !== lastestCached.param.afterClose.toString())) { return false; }
      if (!param.afterConfirm && lastestCached.param.afterConfirm
        || param.afterConfirm && !lastestCached.param.afterConfirm
        || (param.afterConfirm && lastestCached.param.afterConfirm && param.afterConfirm.toString() !== lastestCached.param.afterConfirm.toString())) { return false; }
    } catch (error) {
      return false;
    }
    return true;
  }

  protected setInfo(
    cached: {
      message: string;
      param: { title: string, afterClose?: () => void, afterConfirm?: () => void };
      closeSubc?: Subscription;
    },
    cacheIndex: number
  ) {
    if (!this.infoConfirm) { return; }
    this.infoConfirm.message = cached.message;
    if (cached.param) {
      if (cached.param.title) {
        this.infoConfirm.title = cached.param.title;
      }
      if (this.cachedInfos.length > 1) {
        this.cachedInfos[this.cachedInfos.length - 2].closeSubc.unsubscribe();
      }
    }

    let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
    cached.closeSubc = merge(
      this.infoConfirm.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
      this.infoConfirm.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
    ).pipe(take(1)).subscribe(() => {
      if (closeReason === TmModalCloseReason.SUBMIT) {
        if (cached.param && cached.param.afterConfirm) {
          cached.param.afterConfirm();
        }
      } else {
        if (cached.param && cached.param.afterClose) {
          cached.param.afterClose();
        }
      }
      this.cachedInfos.splice(cacheIndex, 1);
      if (this.cachedInfos.length > 0) {
        const cIndex = this.cachedInfos.length - 1;
        const nextCachedAlert = this.cachedInfos[cIndex];
        this.setInfo(nextCachedAlert, cIndex);
        this.infoConfirm?.open();
      }
    });
  }

  protected setPendingDatas() {
    if (this._pendingTitle) { this.title = this._pendingTitle; }
    if (this._pendingMessage) { this.message = this._pendingMessage; }
    if (this._pendingOkBtnLabel) { this.okBtnLabel = this._pendingOkBtnLabel; }
    if (this._pendingCancelBtnLabel) { this.cancelBtnLabel = this._pendingCancelBtnLabel; }
    if (this._pendingPanelClass) { this.panelClass = this._pendingPanelClass; }
    if (this._pendingModalConfig) { this.modalConfig = this._pendingModalConfig; }
  }
}
